export interface TestType {
  id: number;
  code: string;
  name: string;
  description?: string;
  category?: string;
  isActive: boolean;
}

export interface Test {
  id: number;
  testTypeId: number;
  testType?: TestType; // Relación cargada desde el backend
  patientId: number;
  evaluatorId: string;
  testData?: {
    timeSeconds?: number;
    errors?: number;
    percentile?: number | string;
    educationYears?: number;
    classification?: string; // Clasificación única (compatibilidad)
    classifications?: string[]; // Array de clasificaciones (puede ser una o múltiples)
    // Propiedades específicas de Stroop
    palabraScore?: number;
    colorScore?: number;
    palabraColorScore?: number;
    interferenciaScore?: number;
    palabraPercentile?: number | string;
    colorPercentile?: number | string;
    palabraColorPercentile?: number | string;
    interferenciaPercentile?: number | string;
    palabraClassifications?: string[];
    colorClassifications?: string[];
    palabraColorClassifications?: string[];
    interferenciaClassifications?: string[];
    // Propiedades específicas de HVLT-R
    ensayo1Score?: number;
    ensayo2Score?: number;
    ensayo3Score?: number;
    ensayo4Score?: number;
    recuerdoTotalScore?: number;
    recuerdoTardioScore?: number;
    ensayo1Words?: string[];
    ensayo2Words?: string[];
    ensayo3Words?: string[];
    ensayo4Words?: string[];
    ensayo1Percentile?: number | string;
    ensayo2Percentile?: number | string;
    ensayo3Percentile?: number | string;
    ensayo4Percentile?: number | string;
    recuerdoTotalPercentile?: number | string;
    recuerdoTardioPercentile?: number | string;
    ensayo1Classifications?: string[];
    ensayo2Classifications?: string[];
    ensayo3Classifications?: string[];
    ensayo4Classifications?: string[];
    recuerdoTotalClassifications?: string[];
    recuerdoTardioClassifications?: string[];
    // Propiedades específicas de D2
    trScore?: number;
    taScore?: number;
    oScore?: number;
    cScore?: number;
    trPlusScore?: number;
    trMinusScore?: number;
    varScore?: number;
    totScore?: number;
    errorRate?: number;
    concentrationScore?: number;
    trPercentile?: number | string;
    taPercentile?: number | string;
    oPercentile?: number | string;
    cPercentile?: number | string;
    totPercentile?: number | string;
    trPlusPercentile?: number | string;
    trMinusPercentile?: number | string;
    varPercentile?: number | string;
    concentrationPercentile?: number | string;
    trClassifications?: string[];
    taClassifications?: string[];
    oClassifications?: string[];
    cClassifications?: string[];
    totClassifications?: string[];
    trPlusClassifications?: string[];
    trMinusClassifications?: string[];
    varClassifications?: string[];
    concentrationClassifications?: string[];
    // Propiedades específicas de Elevator Appendix 3B
    elevatorVersionAResponses?: boolean[];
    elevatorVersionBResponses?: boolean[];
    elevatorVersionAScore?: number;
    elevatorVersionBScore?: number;
    elevatorVersionAPercentile?: number | string;
    elevatorVersionBPercentile?: number | string;
    elevatorVersionAClassifications?: string[];
    elevatorVersionBClassifications?: string[];
    // Propiedades específicas de IFS-Ch (8 subtests + total + índice de memoria de trabajo)
    luriaMotorSeriesScore?: number;
    conflictingInstructionsScore?: number;
    goNoGoScore?: number;
    backwardsDigitSpanScore?: number;
    monthsBackwardsScore?: number;
    modifiedCorsiScore?: number;
    proverbInterpretationScore?: number;
    modifiedHaylingScore?: number;
    luriaMotorSeriesPercentile?: number | string;
    conflictingInstructionsPercentile?: number | string;
    goNoGoPercentile?: number | string;
    backwardsDigitSpanPercentile?: number | string;
    monthsBackwardsPercentile?: number | string;
    modifiedCorsiPercentile?: number | string;
    proverbInterpretationPercentile?: number | string;
    modifiedHaylingPercentile?: number | string;
    luriaMotorSeriesZScore?: number;
    conflictingInstructionsZScore?: number;
    goNoGoZScore?: number;
    backwardsDigitSpanZScore?: number;
    monthsBackwardsZScore?: number;
    modifiedCorsiZScore?: number;
    proverbInterpretationZScore?: number;
    modifiedHaylingZScore?: number;
    luriaMotorSeriesClassifications?: string[];
    conflictingInstructionsClassifications?: string[];
    goNoGoClassifications?: string[];
    backwardsDigitSpanClassifications?: string[];
    monthsBackwardsClassifications?: string[];
    modifiedCorsiClassifications?: string[];
    proverbInterpretationClassifications?: string[];
    modifiedHaylingClassifications?: string[];
    ifsChTotalScore?: number;
    ifsChTotalZScore?: number;
    ifsChWorkingMemoryIndex?: number;
    [key: string]: any; // Para datos adicionales específicos de cada test
  } | null;
  observations?: string;
  status: 'completada' | 'en_progreso' | 'cancelada';
  executedAt: string;
  createdAt: string;
  updatedAt: string;
  patient?: {
    id: number;
    firstName: string;
    lastName: string;
    identificationNumber: string;
  };
  evaluator?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

// Helper para obtener el código del tipo de prueba
export const getTestTypeCode = (test: Test): string => {
  return test.testType?.code || '';
};

// Helper para obtener datos del testData con valores por defecto
export const getTestData = (test: Test): any => {
  return {
    timeSeconds: test.testData?.timeSeconds ?? 0,
    errors: test.testData?.errors ?? 0,
    percentile: test.testData?.percentile ?? null,
    educationYears: test.testData?.educationYears ?? null,
    classifications: test.testData?.classifications ?? [],
    ...test.testData
  };
};

export interface TestFormData {
  testTypeId?: number;
  testTypeCode?: string; // Alternativa: usar código en lugar de ID
  patientId: number;
  testData: {
    timeSeconds?: number;
    errors?: number;
    educationYears?: number;
    [key: string]: any;
  };
  observations?: string;
}

export interface TestFilters {
  patientId?: number;
  evaluatorId?: string;
  testTypeId?: number;
  testTypeCode?: string; // Alternativa: filtrar por código
  status?: 'completada' | 'en_progreso' | 'cancelada';
  search?: string;
  page?: number;
  limit?: number;
}

export interface TestListResponse {
  tests: Test[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface TestValidationErrors {
  testType?: string;
  timeSeconds?: string;
  errors?: string;
  observations?: string;
}

export const TEST_TYPE_OPTIONS = [
  { value: 'TMT-A', label: 'TMT-A (Trail Making Test A)', category: 'Atención y Velocidad' },
  { value: 'TMT-B', label: 'TMT-B (Trail Making Test B)', category: 'Atención y Velocidad' },
  { value: 'STROOP', label: 'Test de Stroop', category: 'Control Inhibitorio' },
  { value: 'FLUENCIAS', label: 'Test de Fluencias', category: 'Lenguaje' },
  { value: 'HVLT-R', label: 'HVLT-R (Hopkins Verbal Learning Test)', category: 'Memoria' },
  { value: 'REY-FIGURE', label: 'Figura Compleja de Rey', category: 'Visuoespacial' },
  { value: 'BNT', label: 'Boston Naming Test', category: 'Lenguaje' },
  { value: 'D2', label: 'Test de Atención D2', category: 'Atención' },
  { value: 'ELEVATOR-3B', label: 'Conteo de Elevator 3B', category: 'Atención' },
  { value: 'MOCA', label: 'MoCA (Evaluación Cognitiva de Montreal)', category: 'Cribado Cognitivo Global' },
  { value: 'IFS-CH', label: 'IFS-Ch (INECO Frontal Screening)', category: 'Función Ejecutiva' }
];

export const TEST_STATUS_OPTIONS = [
  { value: 'completada', label: 'Completada' },
  { value: 'en_progreso', label: 'En Progreso' },
  { value: 'cancelada', label: 'Cancelada' }
];

// Configuración específica para cada tipo de prueba
export const TEST_CONFIGS = {
  'TMT-A': {
    name: 'Trail Making Test A',
    description: 'Conecta números en orden secuencial (1-2-3-4...)',
    maxTime: 300, // 5 minutos
    expectedTime: 30, // 30 segundos promedio
    inputType: 'time', // Mide tiempo en segundos
    instructions: [
      'Conecta los números en orden del 1 al 25',
      'Trabaja lo más rápido posible sin cometer errores',
      'No levantes el lápiz del papel',
      'Si cometes un error, continúa desde donde te equivocaste'
    ]
  },
  'TMT-B': {
    name: 'Trail Making Test B',
    description: 'Conecta números y letras alternando (1-A-2-B-3-C...)',
    maxTime: 600, // 10 minutos
    expectedTime: 60, // 1 minuto promedio
    inputType: 'time', // Mide tiempo en segundos
    instructions: [
      'Conecta los números y letras alternando: 1-A-2-B-3-C...',
      'Trabaja lo más rápido posible sin cometer errores',
      'No levantes el lápiz del papel',
      'Si cometes un error, continúa desde donde te equivocaste'
    ]
  },
  'STROOP': {
    name: 'Test de Stroop',
    description: 'Evaluación de control inhibitorio y atención selectiva mediante 3 condiciones.',
    inputType: 'stroop', // Tipo especial con 3 puntuaciones
    conditions: [
      {
        id: 'palabra',
        name: 'Palabra',
        description: 'Lectura de palabras de colores (impresas en tinta negra)',
        placeholder: 'Ej: 112'
      },
      {
        id: 'color',
        name: 'Color',
        description: 'Denominación de colores (parches de colores)',
        placeholder: 'Ej: 84'
      },
      {
        id: 'palabraColor',
        name: 'Palabra-Color',
        description: 'Nombrar el color de la tinta (condición de interferencia)',
        placeholder: 'Ej: 45'
      }
    ],
    instructions: [
      'Registra el número de ítems correctos en cada condición',
      'Condición Palabra: El sujeto lee palabras de colores impresas en negro',
      'Condición Color: El sujeto nombra el color de parches de colores',
      'Condición Palabra-Color: El sujeto nombra el color de la tinta (no lee la palabra)',
      'El índice de interferencia se calcula automáticamente'
    ]
  },
  'FLUENCIAS': {
    name: 'Test de Fluencias',
    description: 'Evaluación de fluencia verbal semántica (animales) y fonológica (letras F, A, S).',
    inputType: 'fluencias', // Tipo especial con 4 puntuaciones
    conditions: [
      {
        id: 'animals',
        name: 'Animals',
        description: 'Fluencia semántica: Generación de nombres de animales',
        placeholder: 'Ej: 23'
      },
      {
        id: 'letterF',
        name: 'Letra F',
        description: 'Fluencia fonológica: Generación de palabras que comienzan con F',
        placeholder: 'Ej: 14'
      },
      {
        id: 'letterA',
        name: 'Letra A',
        description: 'Fluencia fonológica: Generación de palabras que comienzan con A',
        placeholder: 'Ej: 15'
      },
      {
        id: 'letterS',
        name: 'Letra S',
        description: 'Fluencia fonológica: Generación de palabras que comienzan con S',
        placeholder: 'Ej: 12'
      }
    ],
    instructions: [
      'Registra el número de palabras correctas en cada categoría',
      'Animals: El sujeto genera nombres de animales durante 1 minuto',
      'Letra F: El sujeto genera palabras que comienzan con F durante 1 minuto',
      'Letra A: El sujeto genera palabras que comienzan con A durante 1 minuto',
      'Letra S: El sujeto genera palabras que comienzan con S durante 1 minuto'
    ]
  },
  'HVLT-R': {
    name: 'Hopkins Verbal Learning Test - Revised',
    description: 'Evaluación de memoria verbal y aprendizaje mediante lista de 12 palabras.',
    inputType: 'hvlt-r', // Tipo especial con entrada dual
    conditions: [
      {
        id: 'ensayo1',
        name: 'Intento 1',
        description: 'Primer intento de aprendizaje',
        placeholder: '0-12'
      },
      {
        id: 'ensayo2',
        name: 'Intento 2',
        description: 'Segundo intento de aprendizaje',
        placeholder: '0-12'
      },
      {
        id: 'ensayo3',
        name: 'Intento 3',
        description: 'Tercer intento de aprendizaje',
        placeholder: '0-12'
      },
      {
        id: 'ensayo4',
        name: 'Intento 4 (Recuerdo Tardío)',
        description: 'Recuerdo después de 20-25 minutos',
        placeholder: '0-12'
      }
    ],
    instructions: [
      'El HVLT-R evalúa memoria verbal mediante una lista de 12 palabras',
      'Ensayos 1-3: Se presentan las palabras y el paciente debe recordarlas',
      'Ensayo 4: Se realiza 20-25 minutos después para evaluar recuerdo tardío',
      'Recuerdo Total: Suma automática de los ensayos 1, 2 y 3',
      'Puedes ingresar el número directamente o escribir/marcar las palabras que el paciente dijo'
    ]
  },
  'REY-FIGURE': {
    name: 'Figura Compleja de Rey',
    description: 'Evaluación de la función visuoespacial y memoria visual mediante la copia y reproducción de memoria de una figura compleja.',
    inputType: 'rey-figure', // Tipo especial con imagen y múltiples puntuaciones
    instructions: [
      'Sube una imagen de la figura de Rey copiada por el paciente',
      'Registra la exactitud de copia (puntuación directa)',
      'Registra el tiempo de copia en minutos',
      'Opcionalmente, registra el tipo de construcción (I, II, III, IV, V, VI)',
      'Registra la exactitud de memoria (puntuación directa de la reproducción de memoria)',
      'Los percentiles se calculan automáticamente basados en la edad del paciente'
    ]
  },
  'BNT': {
    name: 'Boston Naming Test',
    description: 'Evaluación de la capacidad de denominación visual y acceso léxico mediante el nombramiento de objetos presentados en imágenes.',
    inputType: 'bnt', // Tipo especial con lista de palabras y múltiples criterios
    instructions: [
      'El evaluador presenta cada palabra al paciente',
      'Para cada palabra, marca si la respuesta fue correcta o incorrecta',
      'Evalúa también: Clave Semántica, Clave Fonética, Parafasias Fonológicas y Parafasias Verbales',
      'El score final es el número total de respuestas correctas (0-60)',
      'Los percentiles se calculan automáticamente basados en la edad y escolaridad del paciente'
    ]
  },
  'D2': {
    name: 'Test de Atención D2',
    description: 'Evaluación de atención selectiva, efectividad y control de errores.',
    inputType: 'd2',
    conditions: [
      { id: 'TR', name: 'TR', description: 'Total de Respuestas', placeholder: 'Ej: 520' },
      { id: 'TA', name: 'TA', description: 'Total de Aciertos', placeholder: 'Ej: 205' },
      { id: 'O', name: 'O', description: 'Omisiones', placeholder: 'Ej: 9' },
      { id: 'C', name: 'C', description: 'Comisiones', placeholder: 'Ej: 2' },
      { id: 'TR+', name: 'TR+', description: 'Máximo rendimiento (próximamente)', placeholder: 'Opcional' },
      { id: 'TR-', name: 'TR-', description: 'Mínimo rendimiento (próximamente)', placeholder: 'Opcional' },
      { id: 'VAR', name: 'VAR', description: 'Variabilidad (próximamente)', placeholder: 'Opcional' }
    ],
    instructions: [
      'Ingresa TR, TA, O y C como datos obligatorios.',
      'TOT se calcula automáticamente: TR - (O + C).',
      'Porcentaje de Error se calcula automáticamente: 100 * (O + C) / TR.',
      'TR+, TR- y VAR se consideran campos opcionales por ahora (próximamente).',
      'Los percentiles se calculan en tiempo real por métrica cuando hay datos válidos.'
    ]
  },
  'ELEVATOR-3B': {
    name: 'Conteo de Elevator con Distracción - Apéndice 3B',
    description: 'Registro de respuestas correctas en versión A y B, con cálculo percentilar por edad.',
    inputType: 'elevator-3b',
    instructions: [
      'Marca cada ítem como correcto cuando el paciente responda adecuadamente.',
      'Por ahora se usa solo la fila A (B y C no aplican).',
      'Versión A tiene un máximo de 7 respuestas correctas.',
      'Versión B tiene un máximo de 10 respuestas correctas.',
      'El percentil se calcula en tiempo real para cada versión según edad del paciente.'
    ]
  },
  'MOCA': {
    name: 'Evaluación Cognitiva de Montreal (MoCA)',
    description: 'Instrumento de cribado cognitivo global mediante puntaje directo total (0-30).',
    inputType: 'moca', // Tipo especial con un único puntaje directo
    instructions: [
      'Registra el puntaje directo total obtenido por el paciente (0-30 puntos).',
      'El percentil se calcula automáticamente según la edad del paciente (no requiere escolaridad).',
      'Un puntaje ≥26 se considera generalmente dentro de lo normal, aunque la clasificación final depende de la matriz de percentiles elegida.'
    ]
  },
  'IFS-CH': {
    name: 'IFS-Ch (INECO Frontal Screening - Chile)',
    description: 'Cribado de función ejecutiva/frontal mediante 8 subtests puntuados directamente (puntaje total 0-30).',
    inputType: 'ifs-ch',
    conditions: [
      { id: 'luriaMotorSeries', name: 'Series motoras (Luria)', description: 'Series motoras de Luria', placeholder: '0-3', max: 3 },
      { id: 'conflictingInstructions', name: 'Instrucciones conflictivas', description: 'Instrucciones conflictivas', placeholder: '0-3', max: 3 },
      { id: 'goNoGo', name: 'Control inhibitorio motor (Go-No go)', description: 'Go - No go', placeholder: '0-3', max: 3 },
      { id: 'backwardsDigitSpan', name: 'Repetición de dígitos atrás', description: 'Dígitos en orden inverso', placeholder: '0-6', max: 6 },
      { id: 'monthsBackwards', name: 'Meses atrás', description: 'Meses en orden inverso', placeholder: '0-2', max: 2 },
      { id: 'modifiedCorsi', name: 'Memoria de trabajo visual (Corsi)', description: 'Test de Corsi modificado', placeholder: '0-4', max: 4 },
      { id: 'proverbInterpretation', name: 'Refranes', description: 'Interpretación de refranes', placeholder: '0-3', max: 3 },
      { id: 'modifiedHayling', name: 'Control inhibitorio verbal (Hayling)', description: 'Test de Hayling modificado', placeholder: '0-6', max: 6 }
    ],
    instructions: [
      'Registra el puntaje directo de cada uno de los 8 subtests, según la ficha de aplicación.',
      'El Puntaje Total (0-30) y el Índice de Memoria de Trabajo (Dígitos atrás + Corsi, 0-10) se calculan automáticamente.',
      'Cada subtest y el puntaje total obtienen su propio Z-score y percentil, comparados contra la media/DE de sujetos control (no usa baremos por edad/escolaridad).'
    ]
  }
};

// Modo de presentación de resultados (percentil/clasificación) en AddTestModal:
// - 'selector': el evaluador elige una matriz de clasificación (dropdown) y se muestra
//   un único resultado con esa matriz. Es el modo por defecto — no hace falta listar
//   aquí las pruebas que lo usan.
// - 'compare': sin selector; se muestran las 3 matrices (original, normal, non-normal)
//   a la vez para el mismo percentil ya calculado (independiente de la matriz).
// El percentil siempre es independiente de la matriz en todas las pruebas — la matriz
// solo define cómo se etiqueta/clasifica ese percentil — así que cualquier prueba puede
// pasar a 'compare' agregando una entrada aquí, sin tocar su cálculo de percentil.
export type TestResultMode = 'selector' | 'compare';

const TEST_RESULT_MODE: Partial<Record<string, TestResultMode>> = {
  'IFS-CH': 'compare',
};

export const getTestResultMode = (testTypeCode: string): TestResultMode =>
  TEST_RESULT_MODE[testTypeCode] || 'selector';

// Helper para verificar si un test usa puntuación (Stroop, Fluencias, HVLT-R) o tiempo (TMT)
export const isScoreBasedTest = (testTypeCode: string): boolean => {
  return testTypeCode === 'STROOP' || testTypeCode === 'FLUENCIAS' || testTypeCode === 'HVLT-R' || testTypeCode === 'D2' || testTypeCode === 'ELEVATOR-3B' || testTypeCode === 'MOCA' || testTypeCode === 'IFS-CH';
};

// Lista de palabras del HVLT-R (versión estándar)
export const HVLT_R_WORD_LIST = [
  'maestro',
  'baloncesto',
  'lechuga',
  'dentista',
  'tenis',
  'frijol',
  'ingeniero',
  'papa',
  'profesor',
  'golf',
  'maiz',
  'futbol'
];

// Lista de palabras del Boston Naming Test (BNT) con claves semánticas
export interface BNTWord {
  word: string;
  semanticKey: string;
  phoneticKeyLength: number; // Número de letras a destacar
}

export const BNT_WORD_LIST: BNTWord[] = [
  { word: 'Cama', semanticKey: 'un mueble', phoneticKeyLength: 2 },
  { word: 'Árbol', semanticKey: 'Algo que crece en el campo', phoneticKeyLength: 2 },
  { word: 'Lapiz', semanticKey: 'Sirve para escribir', phoneticKeyLength: 2 },
  { word: 'Casa', semanticKey: 'Un tipo de edificio', phoneticKeyLength: 2 },
  { word: 'Silbato (pito)', semanticKey: 'Sirve para soplar', phoneticKeyLength: 2 },
  { word: 'Tijeras', semanticKey: 'Sirve para cortar', phoneticKeyLength: 2 },
  { word: 'Peine', semanticKey: 'Sirve para arreglarse el cabello', phoneticKeyLength: 2 },
  { word: 'Flor', semanticKey: 'Crece en un jardín', phoneticKeyLength: 2 },
  { word: 'Serrucho', semanticKey: 'Lo usa el carpintero', phoneticKeyLength: 2 },
  { word: 'Cepillo de dientes', semanticKey: 'Se usa en la boca', phoneticKeyLength: 2 },
  { word: 'Helicóptero', semanticKey: 'Sirve para viajar por el aire', phoneticKeyLength: 2 },
  { word: 'Escoba', semanticKey: 'Sirve para limpiar', phoneticKeyLength: 2 },
  { word: 'Pulpo', semanticKey: 'Un animal marino', phoneticKeyLength: 2 },
  { word: 'Zanahoria', semanticKey: 'Algo para comer', phoneticKeyLength: 2 },
  { word: 'Percha', semanticKey: 'Se encuentra en un armario', phoneticKeyLength: 2 },
  { word: 'Silla de ruedas', semanticKey: 'se encuetra en un hospital', phoneticKeyLength: 2 },
  { word: 'Camello (dromedario)', semanticKey: 'un animal', phoneticKeyLength: 2 },
  { word: 'Máscara (careta)', semanticKey: 'Parte de un disfraz', phoneticKeyLength: 2 },
  { word: 'Magdalena', semanticKey: 'Algo para comer', phoneticKeyLength: 3 },
  { word: 'Banco', semanticKey: 'Sirve para sentarse', phoneticKeyLength: 2 },
  { word: 'Raqueta', semanticKey: 'Se utiliza en un deporte', phoneticKeyLength: 2 },
  { word: 'Caracol', semanticKey: 'Un animal', phoneticKeyLength: 2 },
  { word: 'Volcán', semanticKey: 'Un tipo de montaña', phoneticKeyLength: 2 },
  { word: 'Caballito de mar', semanticKey: 'Un animal marino', phoneticKeyLength: 2 },
  { word: 'Dardo', semanticKey: 'Sirve para lanzar', phoneticKeyLength: 2 },
  { word: 'Canoa', semanticKey: 'Se usa en el agua', phoneticKeyLength: 2 },
  { word: 'Globo', semanticKey: 'Un tipo de mapa', phoneticKeyLength: 2 },
  { word: 'Corona', semanticKey: 'La usan los reyes', phoneticKeyLength: 2 },
  { word: 'Castor', semanticKey: 'Un animal', phoneticKeyLength: 2 },
  { word: 'Armónica', semanticKey: 'Un instrumento musical', phoneticKeyLength: 2 },
  { word: 'Rinoceronte', semanticKey: 'Un animal', phoneticKeyLength: 2 },
  { word: 'Bellota', semanticKey: 'Proviene de un árbol', phoneticKeyLength: 2 },
  { word: 'Iglú', semanticKey: 'Un tipo de casa', phoneticKeyLength: 2 },
  { word: 'Zancos', semanticKey: 'Se usan para caminar más alto', phoneticKeyLength: 2 },
  { word: 'Dominó', semanticKey: 'Un juego', phoneticKeyLength: 2 },
  { word: 'Cactús', semanticKey: 'Algo que crece', phoneticKeyLength: 2 },
  { word: 'Escalera mecánica', semanticKey: 'sirve para subir', phoneticKeyLength: 2 },
  { word: 'Arpa', semanticKey: 'Un instrumento musical', phoneticKeyLength: 2 },
  { word: 'Hamaca', semanticKey: 'Sirve para descansar', phoneticKeyLength: 2 },
  { word: 'Chupete', semanticKey: 'Lo usan los bebés', phoneticKeyLength: 3 },
  { word: 'Pelícano', semanticKey: 'Un Ave', phoneticKeyLength: 2 },
  { word: 'Fonendoscopio', semanticKey: 'Lo usan los médicos', phoneticKeyLength: 2 },
  { word: 'Pirámide', semanticKey: 'Se encuentra en Egipto', phoneticKeyLength: 2 },
  { word: 'Bozal', semanticKey: 'Se utiliza para los perros', phoneticKeyLength: 2 },
  { word: 'Unicornio', semanticKey: 'Animal mítico', phoneticKeyLength: 2 },
  { word: 'Embudo', semanticKey: 'Sirve para verte un liquido', phoneticKeyLength: 2 },
  { word: 'Acordeón', semanticKey: 'Un instrumento musical', phoneticKeyLength: 2 },
  { word: 'Aguja', semanticKey: 'Se usa para coser', phoneticKeyLength: 1 },
  { word: 'Espárrago', semanticKey: 'Algo para comer', phoneticKeyLength: 2 },
  { word: 'Compás', semanticKey: 'Sirve para dibujar', phoneticKeyLength: 2 },
  { word: 'Cerradura', semanticKey: 'se usa para abrir la puerta', phoneticKeyLength: 2 },
  { word: 'Trípode', semanticKey: 'Lo usan los fotógrafos', phoneticKeyLength: 3 },
  { word: 'Pergamino', semanticKey: 'Un documento', phoneticKeyLength: 3 },
  { word: 'Pinzas', semanticKey: 'Un utensilio', phoneticKeyLength: 3 },
  { word: 'Esfinge', semanticKey: 'Se encuentra en Egipto', phoneticKeyLength: 2 },
  { word: 'Yugo', semanticKey: 'Se utiliza para animales de tiro', phoneticKeyLength: 2 },
  { word: 'Regadera', semanticKey: 'Se usa en el jardín', phoneticKeyLength: 2 },
  { word: 'Paleta', semanticKey: 'La usan los artistas', phoneticKeyLength: 2 },
  { word: 'Transportador', semanticKey: 'Sirve para medir águlos', phoneticKeyLength: 5 },
  { word: 'Ábaco', semanticKey: 'Sirve para contar', phoneticKeyLength: 1 }
];

// Función para obtener las partes de la palabra con clave fonética destacada
export const getPhoneticKeyParts = (word: string, length: number): { phoneticKey: string; rest: string } => {
  const cleanWord = word.split(' ')[0]; // Tomar solo la primera palabra (antes del paréntesis si existe)
  const phoneticKey = cleanWord.substring(0, length);
  const rest = cleanWord.substring(length);
  
  return { phoneticKey, rest };
};

// Helper para calcular Recuerdo Total (suma de ensayos 1, 2, 3)
export const calculateHVLTRTotalRecall = (
  ensayo1: number,
  ensayo2: number,
  ensayo3: number
): number => {
  return (ensayo1 || 0) + (ensayo2 || 0) + (ensayo3 || 0);
};

// Helper para calcular el índice de interferencia del Stroop
export const calculateStroopInterference = (
  palabraScore: number,
  colorScore: number,
  palabraColorScore: number
): number => {
  // Fórmula de interferencia: PC' - PC
  // donde PC' = (P × C) / (P + C) (predicción basada en Palabra y Color)
  if (palabraScore <= 0 || colorScore <= 0) return 0;
  const predicted = (palabraScore * colorScore) / (palabraScore + colorScore);
  return Math.round((palabraColorScore - predicted) * 100) / 100;
};


// Función para obtener la clasificación desde testData
// DEPRECATED: Usar getClassificationsFromTest en su lugar
export const getClassificationFromTest = (test: Test): string | string[] | null => {
  const testData = getTestData(test);
  // Si hay múltiples clasificaciones, retornar el array
  if (testData.classifications && Array.isArray(testData.classifications) && testData.classifications.length > 0) {
    return testData.classifications.length > 1 ? testData.classifications : testData.classifications[0];
  }
  // Si no hay clasificaciones, retornar null
  return null;
};

// Helper para obtener clasificaciones como array (siempre retorna array)
export const getClassificationsFromTest = (test: Test): string[] => {
  const testData = getTestData(test);
  if (testData.classifications && Array.isArray(testData.classifications) && testData.classifications.length > 0) {
    return testData.classifications;
  }
  // Si no hay classifications, retornar array vacío
  return [];
};

// Función para formatear el tiempo
export const formatTime = (timeSeconds: number): string => {
  const minutes = Math.floor(timeSeconds / 60);
  const seconds = Math.floor(timeSeconds % 60);
  
  if (minutes > 0) {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  } else {
    return `${seconds}s`;
  }
};
